import React from "react";
import { useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { useInstruction } from "@/hooks/useInstruction";
import { Field, TextareaField, YesNo, SectionTitle, SubHead } from "@/components/wizard/Fields";
import { ListEditor } from "@/components/wizard/Steps";
import { DisclaimerSection } from "@/components/DisclaimerSection";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft } from "lucide-react";

const grid = "grid gap-4 sm:grid-cols-2";

function Settlor({ v = {}, on, prefix }) {
  return (
    <div className={grid}>
      <Field label="Full name" value={v.fullName} onChange={(x) => on("fullName", x)} testId={`${prefix}-name`} />
      <Field label="NI Number" value={v.ni} onChange={(x) => on("ni", x)} testId={`${prefix}-ni`} />
      <Field label="Date of Birth" type="date" value={v.dob} onChange={(x) => on("dob", x)} testId={`${prefix}-dob`} />
      <Field label="Telephone" value={v.tel} onChange={(x) => on("tel", x)} testId={`${prefix}-tel`} />
      <Field label="Email" type="email" value={v.email} onChange={(x) => on("email", x)} testId={`${prefix}-email`} />
      <Field label="Address" value={v.address} onChange={(x) => on("address", x)} testId={`${prefix}-address`} />
    </div>
  );
}

export default function LegacyTrustForm() {
  const navigate = useNavigate();
  const { data, setData, progress, saving, save, submit } = useInstruction("legacy_trust");

  if (data === null)
    return <div className="min-h-screen flex items-center justify-center bg-brand-bg"><Loader2 className="w-8 h-8 animate-spin text-brand-green" /></div>;

  const patch = (section, obj) => setData((d) => ({ ...d, [section]: { ...(d[section] || {}), ...obj } }));
  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setList = (k, arr) => setData((d) => ({ ...d, [k]: arr }));

  return (
    <div className="min-h-screen bg-brand-bg">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <button onClick={() => navigate("/dashboard")} className="text-sm text-brand-muted hover:text-brand-green flex items-center gap-1 mb-4" data-testid="trust-back">
          <ChevronLeft className="w-4 h-4" /> Back to dashboard
        </button>
        <div className="bg-white rounded-xl border border-brand-line p-6 md:p-10 shadow-sm space-y-2">
          <SectionTitle hint="Legacy Protection Trust — £2,950 + VAT (£3,540 inc VAT).">Legacy Protection Trust Instruction</SectionTitle>

          <Field label="Name of Trust" value={(data.trust || {}).name} onChange={(x) => patch("trust", { name: x })} testId="trust-name" />
          <Field label="Property address" value={(data.trust || {}).propertyAddress} onChange={(x) => patch("trust", { propertyAddress: x })} testId="trust-prop-address" />

          <SubHead>Settlor 1 (Fund A)</SubHead>
          <Settlor v={data.settlor1 || {}} prefix="settlor1" on={(k, val) => patch("settlor1", { [k]: val })} />
          <div className="pt-3"><YesNo label="Is there a second settlor (Fund B)?" value={data.hasSettlor2} onChange={(x) => setField("hasSettlor2", x)} testId="trust-has-settlor2" /></div>
          {data.hasSettlor2 && (<><SubHead>Settlor 2 (Fund B)</SubHead><Settlor v={data.settlor2 || {}} prefix="settlor2" on={(k, val) => patch("settlor2", { [k]: val })} /></>)}

          <SubHead>Trustees</SubHead>
          <YesNo label="Are the settlors to be trustees?" value={(data.trustees || {}).settlorsAreTrustees} onChange={(x) => patch("trustees", { settlorsAreTrustees: x })} testId="trust-settlors-trustees" />
          <div className="pt-2"><YesNo label="Appoint a professional trustee?" value={(data.trustees || {}).professionalTrustee} onChange={(x) => patch("trustees", { professionalTrustee: x })} testId="trust-prof-trustee" /></div>
          <div className="pt-2"><YesNo label="Appoint a Protector?" value={(data.trustees || {}).protector} onChange={(x) => patch("trustees", { protector: x })} testId="trust-protector" /></div>
          <div className="pt-3">
            <ListEditor items={data.additionalTrustees} setItems={(x) => setList("additionalTrustees", x)} testId="trustee"
              blank={{ name: "", ni: "", address: "", email: "", phone: "" }} addLabel="Add additional trustee (max 4)"
              render={(it, i, update) => (
                <div className={grid}>
                  <Field label="Name" value={it.name} onChange={(v) => update(i, "name", v)} testId={`trustee-name-${i}`} />
                  <Field label="NI Number" value={it.ni} onChange={(v) => update(i, "ni", v)} testId={`trustee-ni-${i}`} />
                  <Field label="Address" value={it.address} onChange={(v) => update(i, "address", v)} testId={`trustee-address-${i}`} />
                  <Field label="Email" value={it.email} onChange={(v) => update(i, "email", v)} testId={`trustee-email-${i}`} />
                  <Field label="Phone" value={it.phone} onChange={(v) => update(i, "phone", v)} testId={`trustee-phone-${i}`} />
                </div>
              )} />
          </div>

          <SubHead>Property to be placed in trust</SubHead>
          <div className={grid}>
            <Field label="Present value (£)" type="number" value={(data.property || {}).value} onChange={(x) => patch("property", { value: x })} testId="trust-prop-value" />
            <Field label="Year purchased" value={(data.property || {}).yearPurchased} onChange={(x) => patch("property", { yearPurchased: x })} testId="trust-prop-year" />
            <Field label="Ownership % — Settlor 1" value={(data.property || {}).ownership1} onChange={(x) => patch("property", { ownership1: x })} testId="trust-own1" />
            <Field label="Ownership % — Settlor 2" value={(data.property || {}).ownership2} onChange={(x) => patch("property", { ownership2: x })} testId="trust-own2" />
            <Field label="Title number" value={(data.property || {}).titleNumber} onChange={(x) => patch("property", { titleNumber: x })} testId="trust-title" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <YesNo label="Is the property registered?" value={(data.property || {}).registered} onChange={(x) => patch("property", { registered: x })} testId="trust-registered" />
            <YesNo label="Is the property mortgaged / equity release?" value={(data.property || {}).mortgaged} onChange={(x) => patch("property", { mortgaged: x })} testId="trust-mortgaged" />
            <YesNo label="Is the property freehold?" value={(data.property || {}).freehold} onChange={(x) => patch("property", { freehold: x })} testId="trust-freehold" />
          </div>

          <SubHead>Beneficiaries</SubHead>
          <YesNo label="Include spouse / partner as beneficiary?" value={(data.beneficiariesTrust || {}).spouse} onChange={(x) => patch("beneficiariesTrust", { spouse: x })} testId="trust-ben-spouse" />
          <div className="pt-3">
            <ListEditor items={data.childrenTrust} setItems={(x) => setList("childrenTrust", x)} testId="tchild"
              blank={{ fullName: "", address: "", postcode: "", dob: "", ageToInherit: "" }} addLabel="Add a child / beneficiary"
              render={(it, i, update) => (
                <div className={grid}>
                  <Field label="Full name" value={it.fullName} onChange={(v) => update(i, "fullName", v)} testId={`tchild-name-${i}`} />
                  <Field label="Address" value={it.address} onChange={(v) => update(i, "address", v)} testId={`tchild-address-${i}`} />
                  <Field label="Postcode" value={it.postcode} onChange={(v) => update(i, "postcode", v)} testId={`tchild-postcode-${i}`} />
                  <Field label="Date of Birth" type="date" value={it.dob} onChange={(v) => update(i, "dob", v)} testId={`tchild-dob-${i}`} />
                  <Field label="Age to inherit" value={it.ageToInherit} onChange={(v) => update(i, "ageToInherit", v)} testId={`tchild-age-${i}`} />
                </div>
              )} />
          </div>
          <TextareaField label="Distribution of income & capital (beneficiary and %)" value={data.distribution} onChange={(x) => setField("distribution", x)} testId="trust-distribution" />

          <div className="pt-4"><SubHead>Declarations & Elected Services</SubHead>
            <DisclaimerSection variant="trust" value={data.disclaimer || {}} onChange={(v) => setField("disclaimer", v)} />
          </div>
          <p className="text-xs text-brand-muted pt-2">Certified proof of ID and proof of address are required for all settlors and trustees — please upload these from your dashboard.</p>
        </div>

        <div className="flex items-center justify-between mt-6">
          <Button variant="ghost" onClick={() => save(false)} disabled={saving} className="text-brand-muted" data-testid="trust-save-draft">Save draft</Button>
          <div className="flex gap-2">
            {progress?.submitted ? (
              <Button disabled className="bg-brand-green text-white">Submitted</Button>
            ) : (
              <>
                <Button variant="outline" onClick={async () => { if (await save(true)) navigate("/dashboard"); }} disabled={saving} className="border-brand-line text-brand-green" data-testid="trust-complete">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save & complete
                </Button>
                <Button onClick={async () => { if (await save(true, true)) await submit(); }} disabled={saving} className="bg-brand-green hover:bg-brand-greenHover text-white" data-testid="trust-submit">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Submit instruction
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
